import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation resources (Bosnian=bs, Croatian=hr, English=en, Serbian=sr)
import commonBS from './resources/bs/common.json';
import commonHR from './resources/hr/common.json';
import commonEN from './resources/en/common.json';
import commonSR from './resources/sr/common.json';

export const defaultLanguage = 'bs';
export const fallbackLanguage = 'en';

export const supportedLanguages = {
  bs: 'Bosanski',
  hr: 'Hrvatski',
  sr: 'Srpski',
  en: 'English'
} as const;

export type SupportedLanguage = keyof typeof supportedLanguages;

// Custom detector: map legacy "bhs" (ISO 639-3) to "bs" (ISO 639-1) so Bosnian resolves
const bhsToBsDetector = {
  name: 'bhsToBs',
  lookup(options: { lookupLocalStorage?: string }) {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return undefined;
      const key = options.lookupLocalStorage || 'i18nextLng';
      const stored = window.localStorage.getItem(key);
      if (stored === 'bhs' || (stored && stored.startsWith('bhs-'))) return 'bs';
    } catch (_) {}
    return undefined;
  },
  cacheUserLanguage() {},
};

// Language detection configuration
const detectorOptions = {
  order: ['bhsToBs', 'localStorage', 'navigator'],
  lookupLocalStorage: 'i18nextLng',
  caches: ['localStorage'],
  excludeCacheFor: ['cimode'],
};

export const initI18n = (namespaceResources?: Record<string, any>) => {
  const firstLangResources = namespaceResources?.bs || namespaceResources?.en || {};
  const extraNamespaces = Object.keys(firstLangResources);
  const defaultNS =
    extraNamespaces.length > 0 ? (['common', ...extraNamespaces] as const) : 'common';

  const resources = {
    bs: {
      common: commonBS,
      ...(namespaceResources?.bs || {})
    },
    hr: {
      common: commonHR,
      ...(namespaceResources?.hr || {})
    },
    sr: {
      common: commonSR,
      ...(namespaceResources?.sr || {})
    },
    en: {
      common: commonEN,
      ...(namespaceResources?.en || {})
    }
  };

  const languageDetector = new LanguageDetector();
  languageDetector.addDetector(bhsToBsDetector);

  return i18n
    .use(languageDetector)
    .use(initReactI18next)
    .init({
      resources,
      defaultNS,
      fallbackLng: fallbackLanguage,
      lng: defaultLanguage,
      supportedLngs: ['bs', 'hr', 'sr', 'en'],

      detection: detectorOptions,

      interpolation: {
        escapeValue: false, // React already escapes values
      },

      react: {
        useSuspense: false, // Disable suspense for SSR compatibility
      },

      // Return key if translation is missing
      saveMissing: false,
      returnEmptyString: false,

      // Debug mode (disable in production)
      debug: import.meta.env.DEV,
    });
};

export default i18n;
